import React, { forwardRef } from "react";
import { Link as InternalLink } from "gatsby";
import scrollTo from "gatsby-plugin-smoothscroll";
import { Location } from "@reach/router";

const Link = forwardRef(
  (
    {
      external,
      children,
      href,
      className,
      location,
      activeStyle,
      onClick,
      ...rest
    },
    ref,
  ) => {
    if (external)
      return (
        <a
          ref={ref}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={onClick}
          {...rest}
        >
          {children}
        </a>
      );

    // smooth scroll for same-page hash links
    const position = href?.indexOf("#") ?? -1;
    if (position !== -1 && href.substring(0, position) === location?.pathname) {
      const hash = href.substring(position);
      const handleClick = (event) => {
        scrollTo(hash);
        if (typeof onClick === "function") {
          onClick(event);
        }
      };

      return (
        <span
          ref={ref}
          role="link"
          tabIndex={0}
          className={className}
          {...rest}
          onClick={handleClick}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleClick(event);
            }
          }}
        >
          {children}
        </span>
      );
    }

    return (
      <InternalLink
        ref={ref}
        to={href}
        className={className}
        activeStyle={activeStyle}
        onClick={onClick}
        {...rest}
      >
        {children}
      </InternalLink>
    );
  },
);

Link.displayName = "Link";

const LinkWithLocation = forwardRef((props, ref) => {
  return (
    <Location>
      {({ location }) => <Link ref={ref} location={location} {...props} />}
    </Location>
  );
});

LinkWithLocation.displayName = "LinkWithLocation";

export default LinkWithLocation;
